import sanitizeHtml, { type Attributes, type Tag } from 'sanitize-html';

import { Category } from '../../types';
import { isDevOrTest } from '../environment';

export const allowVulnerableTags = true;

export const defaultAllowedClasses: { [key: string]: string[]; } = {
  article: ['rw-embedded-tweet', 'rw-embed', 'rw-discussion-post', 'rw-discussion-post-data', 'rw-discussion-post-content'],
  hr: ['twitter-thread-delimiter'],
  p: ['rw-outer-content', 'rw-ai', 'rw-hide'],
  span: ['rw-ai', 'rw-ai-explainer', 'rw-ai-highlighted', 'rw-podcast-timestamp'],
  li: ['rw-ai'],
  div: ['rw-digest-thread-wrapper', 'rw-embed-wrapper', 'rw-soundcloud-wrapper', 'rw-embed-title', 'rw-podcast-timestamp'],
  img: ['rw-embed-img'],
};

export const defaultAllowedSchemesByTag = {
  img: sanitizeHtml.defaults.allowedSchemes.concat(['data']),
};

export function getDefaultAllowedTags() {
  return sanitizeHtml.defaults.allowedTags.concat([
    'img',
    'image',
    'video',
    'source',
    'svg',
    'path',
    'picture',
    'center',
    'embed',
    'details',
    'summary',
  ]);
}

export function getDefaultAllowedAttributes(
  attributesAllowedOnAnyTag: string[],
  allowImageStyles: boolean,
) {
  return {
    a: ['href', 'name', 'target', 'rel', 'data-rw-button'],
    img: ['src', 'referrerpolicy', ...(allowImageStyles ? ['style', 'width', 'height', 'class'] : [])],
    image: ['xlink:href', ...(allowImageStyles ? ['style', 'width', 'height', 'class'] : [])],
    source: ['src', 'type', 'srcset', 'media'],
    video: ['controls', 'src', 'preload', 'playsinline'],
    embed: ['src'],
    path: ['*'],
    svg: ['*'],
    ol: ['start'],
    th: ['rowspan', 'colspan'],
    td: ['rowspan', 'colspan'],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '*': attributesAllowedOnAnyTag,
  };
}

export function getDefaultTransformTags(allowImageStyles: boolean, category: Category) {
  return {
    source: (tagName: string, attribs: Attributes): Tag => {
      // Proxy Twitter MP4 video sources through our server to avoid CORS issues.
      // HLS (.m3u8) sources are removed entirely via exclusiveFilter in the sanitize options
      // (Chrome returns "maybe" for canPlayType('application/x-mpegURL') and tries them anyway).
      const srcPath = attribs.src?.split('?')[0] ?? '';
      if (srcPath.includes('video.twimg.com') && !srcPath.endsWith('.m3u8')) {
        // Use VITE_SERVER_BASE_URL in Vite contexts (web app, content frame) or
        // EXPO_SERVER_BASE_URL in native mobile builds (inlined by babel.config.js).
        // Falls back to readwise.io in production where neither is set.
        const proxyBase = (
          process.env.VITE_SERVER_BASE_URL ||
          process.env.EXPO_SERVER_BASE_URL
        )?.replace(/\/+$/, '') ?? 'https://readwise.io';
        attribs.src = `${proxyBase}/reader/video-proxy/?url=${encodeURIComponent(attribs.src)}`;
      }
      return {
        tagName,
        attribs,
      };
    },
    video: (tagName: string, attribs: Attributes): Tag => {
      // Don't auto-load videos to save bandwidth - user must click play
      attribs.preload = 'none';
      // Required for inline video playback on iOS Safari/WKWebView
      attribs.playsinline = '';
      return {
        tagName,
        attribs,
      };
    },
    embed: (tagName: string, attribs: Attributes): Tag => {
      const youtubeDomains = ['youtube.com', 'youtu.be', 'm.youtube.com'];
      const isYoutube = youtubeDomains.some((domain) => attribs.src?.includes(domain));

      if (isYoutube) {
        const url = new URL(attribs.src);
        const params = new URLSearchParams(url.search);
        params.delete('autoplay');
        params.delete('playsinline');
        url.search = params.toString();
        attribs.src = url.toString();
      }

      return {
        tagName,
        attribs,
      };
    },
    svg(tagName: string, attribs: Attributes): Tag {
      const viewBox = attribs.viewbox;
      if (viewBox) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_x, _y, width, height] = viewBox.replace(',', '').split(' ');
          attribs.width = width;
          attribs.height = height;
        } catch {
          // do nothing
        }
      } else {
        // Pick some default size for this SVG, so it isn't humungous.
        attribs.width = '32';
        attribs.height = '32';
      }
      return {
        tagName,
        attribs,
      };
    },
    img: (tagName: string, attribs: Attributes) => {
      attribs.referrerpolicy = 'no-referrer';
      if (allowImageStyles) {
        attribs.class = 'allow-image-styles';

        // Preserve width/height properties _unless_ they're a % as they'll likely break our formatting
        // because we don't preserve their parent properties often...
        if (typeof attribs.height === 'string') {
          // In case of height attribute, if its set to specific value, we replace height with max-height, to preserve image ratio when on mobile or small screen device
          if (!attribs.height.includes('%')) {
            attribs.style = attribs.style
              ? `${attribs.style}; max-height: ${attribs.height}px;`
              : `max-height: ${attribs.height}px;`;
          }
          delete attribs.height;
        }
        // If style attribute includes height, change it to max-height
        if (typeof attribs.style === 'string') {
          if (attribs.style.startsWith('height:')) {
            attribs.style = attribs.style.replace('height:', 'max-height:');
          }
          if (attribs.style.includes(' height:')) {
            attribs.style = attribs.style.replace(' height:', ' max-height:');
          }
          if (attribs.style.includes(';height:')) {
            attribs.style = attribs.style.replace(';height:', ';max-height:');
          }
        }
        if (typeof attribs.width === 'string' && attribs.width.includes('%')) {
          delete attribs.width;
        }
        if (attribs.style?.includes('width: 100%')) {
          attribs.style = attribs.style.replace('width: 100%', '');
        }
        if (attribs.style?.includes('height: 100%')) {
          attribs.style = attribs.style.replace('height: 100%', '');
        }
      }
      // For devs: Uncomment this if you want images to refresh every time
      if (isDevOrTest) {
        // console.log("Don't forget to comment me back out!");
        // const timestamp = new Date().getTime();
        // attribs.src = `${attribs.src}?cache_bust=${timestamp}`;
      }
      return {
        attribs,
        tagName: 'img',
      };
    },
    a: (tagName: string, attribs: Attributes) => {
      const isInternalLink = attribs?.href?.startsWith('#');

      if (!isInternalLink) {
        attribs.target = '_blank';
      }
      attribs.rel = 'noreferrer';
      return {
        attribs,
        tagName: 'a',
      };
    },
  };
}
